// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import React, { useEffect, useCallback, useMemo, memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../../store';
import { useMsal } from '../../../../recruiter/useMsal';
import { Sidebar } from '../../../../components/Sidebar';
import styles from './jobsRoles.module.scss';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PageLoader from '../../../../components/PageLoader';
import { searchCandidatesGlobal } from '../../../../store/interviewHostSlice';
import { fetchInterviewHostDashboard, Job } from '../../../../store/interviewHostSlice';
import NewJobModal from '../NewJobModal';
import DashboardDetailsModal, { DetailKind } from './DashboardDetailsModal';
import { formatNumber, getDeltaSign } from '../../../../utils/numberUtils';

// Type Definitions
interface MetricCard {
  value: string;
  label: string;
  subtext: string;
  detail: DetailKind;
}

type BadgeColor = 'warning' | 'success' | 'info' | 'error';

interface StatusBadge {
  text: string;
  color: BadgeColor;
}

// Utility Functions - Moved outside component for better performance
const parsePipelineLabel = (label: string | undefined | null): StatusBadge => {
  if (!label) {
    return { text: 'N/A', color: 'info' };
  }

  const lowerLabel = label.toLowerCase();

  if (lowerLabel.includes('to review') || lowerLabel.includes('report ready')) {
    return { text: label, color: 'warning' };
  }
  if (lowerLabel.includes('approved') || lowerLabel.includes('completed')) {
    return { text: label, color: 'success' };
  }
  if (lowerLabel.includes('invited')) {
    return { text: label, color: 'info' };
  }
  if (lowerLabel.includes('declined') || lowerLabel.includes('decided')) {
    return { text: label, color: 'error' };
  }

  return { text: label, color: 'info' };
};

const getStatusClassName = (status: string): string => {
  return status.toLowerCase() === 'active' ? styles.statusOpen : styles.statusOnHold;
};

const getBadgeClassName = (color: BadgeColor): string => {
  const badgeMap: Record<BadgeColor, string> = {
    warning: styles.badgeWarning,
    success: styles.badgeSuccess,
    info: styles.badgeInfo,
    error: styles.badgeError
  };
  return badgeMap[color] || styles.badgeWarning;
};

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const numberOrZero = (value: unknown): number => (isFiniteNumber(value) ? value : 0);

// Memoized Sub-Components
const MetricCardComponent = memo(
  ({ metric, onSelect }: { metric: MetricCard; onSelect: (detail: DetailKind) => void }) => (
    <div
      className={`${styles.metricCard} ${styles.metricCardClickable}`}
      role="button"
      tabIndex={0}
      aria-label={`${metric.label}: view details`}
      onClick={() => onSelect(metric.detail)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(metric.detail);
        }
      }}
    >
      <div className={styles.metricValue}>{metric.value}</div>
      <div className={styles.metricLabel}>{metric.label}</div>
      <div className={styles.metricSubtext}>{metric.subtext}</div>
    </div>
  )
);
MetricCardComponent.displayName = 'MetricCard';

interface JobCardProps {
  job: Job;
  onJobClick: (jobId: string) => void;
}

const JobCardComponent = memo(({ job, onJobClick }: JobCardProps) => {
  const statusBadge = useMemo(() => parsePipelineLabel(job.pipeline_label), [job.pipeline_label]);

  const handleClick = useCallback(() => {
    onJobClick(job.job_id);
  }, [job.job_id, onJobClick]);

  return (
    <div className={styles.jobCard} onClick={handleClick}>
      <div className={styles.jobHeader}>
        <div className={styles.jobHeaderLeft}>
          <span className={`${styles.status} ${getStatusClassName(job.status)}`}>{job.status}</span>
          <span className={styles.postedTime}>Posted {job.posted_days_ago}d ago</span>
        </div>
      </div>

      <h3 className={styles.jobTitle}>{job.title}</h3>

      <p className={styles.jobMeta}>
        {job.department} · {job.location}
        {job.business_unit && ` · ${job.business_unit}`}
      </p>

      <div className={styles.jobFooter}>
        <div className={styles.jobFooterLeft}>
          <PeopleOutlineIcon sx={{ fontSize: 16 }} />
          <span>{job.candidate_count} candidates</span>
        </div>
        <div className={`${styles.badge} ${getBadgeClassName(statusBadge.color)}`}>{statusBadge.text}</div>
      </div>

      <div className={styles.progressBar}>
        <div className={styles.progressFill} style={{ width: `${job.progress_percent}%` }} />
      </div>
    </div>
  );
});
JobCardComponent.displayName = 'JobCard';

// Main Component
const JobsRoles: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { instance } = useMsal();
  const user = useSelector((state: RootState) => state.auth.user);
  const { data: dashboardData, loading, error } = useSelector((state: RootState) => state.interviewHost);
  const { globalSearchResults, globalSearchLoading } = useSelector((state: RootState) => state.interviewHost);
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [detailKind, setDetailKind] = useState<DetailKind | null>(null);

  // Fetch fresh data on every visit to the page
  useEffect(() => {
    dispatch(fetchInterviewHostDashboard());
  }, [dispatch]);

  // Instant search - triggers immediately as user types (like original job search)
  useEffect(() => {
    // Clear search if query is empty
    if (!searchQuery.trim()) {
      setHasSearched(false);
      return;
    }

    // Trigger search immediately for instant feedback
    dispatch(searchCandidatesGlobal({ searchQuery: searchQuery.trim() }));
    setHasSearched(true);
  }, [searchQuery, dispatch]);

  // Event handlers - Memoized to prevent unnecessary re-renders
  const handleLogout = useCallback(async () => {
    try {
      window.location.href = '/api/sso/logout';
    } catch (err) {
      console.error('Logout failed:', err);
    }
  }, []);

  const handleBackToHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchInterviewHostDashboard());
  }, [dispatch]);

  const handleJobClick = useCallback(
    (jobId: string) => {
      navigate(`/jobs/${jobId}/candidates`);
    },
    [navigate]
  );

  const closeDetails = useCallback(() => setDetailKind(null), []);

  const handleOpenCandidate = useCallback(
    (item: {
      candidate_id: string;
      job_id: string;
      name: string;
      source?: string;
      completed_source?: string | null;
    }) => {
      // Finished candidates open their report; everyone else opens their role's list.
      const finishedAs = detailKind === 'completed' ? item.source : item.completed_source;
      if (finishedAs) {
        const report = finishedAs === 'Skill assessment' ? 'skill-assessment-report' : 'report';
        navigate(`/jobs/${item.job_id}/candidates/${item.candidate_id}/${report}`);
        return;
      }
      // Open the role's candidates filtered to this person, with their card expanded.
      navigate(
        `/jobs/${item.job_id}/candidates?search=${encodeURIComponent(item.name)}&candidate=${encodeURIComponent(item.candidate_id)}`
      );
    },
    [detailKind, navigate]
  );

  const handleNewJobSuccess = useCallback(() => {
    dispatch(fetchInterviewHostDashboard());
  }, [dispatch]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setHasSearched(false);
  }, []);

  // Show search results if searched, otherwise show all jobs
  const visibleJobs = useMemo(() => {
    if (hasSearched && globalSearchResults && Array.isArray(globalSearchResults.jobs)) {
      // Filter out any jobs with missing required fields
      return globalSearchResults.jobs.filter(
        (job) => job && job.job_id && job.title && job.department && job.location && job.status
      );
    }
    if (!dashboardData) return [];
    return dashboardData.jobs.filter((job) => !job.usage_restricted);
  }, [dashboardData, hasSearched, globalSearchResults]);

  const visibleCandidates = useMemo(() => {
    if (hasSearched && globalSearchResults && Array.isArray(globalSearchResults.candidates)) {
      // Filter out any candidates with missing required fields
      return globalSearchResults.candidates.filter(
        (candidate) => candidate && candidate.candidate_id && candidate.name && candidate.email && candidate.status
      );
    }
    return [];
  }, [hasSearched, globalSearchResults]);

  // Computed metric cards - Memoized to prevent recalculation
  const metricCards = useMemo<MetricCard[]>(() => {
    if (!dashboardData) return [];

    const { metrics } = dashboardData;
    const avgInviteDays = metrics.average_time_to_invite_days ?? metrics.average_interview_host_minutes;
    const avgInviteDelta = metrics.average_time_to_invite_delta_days ?? metrics.average_interview_host_delta_minutes;
    const deltaSign = getDeltaSign(metrics.open_roles_delta_month);
    const avgTimeDeltaSign = getDeltaSign(avgInviteDelta);
    const averageInviteTime = isFiniteNumber(avgInviteDays) ? `${formatNumber(avgInviteDays, 1)}d` : 'N/A';
    const averageInviteDelta = isFiniteNumber(avgInviteDelta)
      ? `${avgTimeDeltaSign} ${formatNumber(Math.abs(avgInviteDelta), 1)}d vs last month`
      : isFiniteNumber(avgInviteDays)
        ? 'Invites this month'
        : 'No invite data';

    return [
      {
        value: dashboardData.jobs.length.toString(),
        label: 'Total roles',
        subtext: `${deltaSign} ${numberOrZero(metrics.open_roles_delta_month)} new openings this month`,
        detail: 'roles'
      },
      {
        value: numberOrZero(metrics.total_candidates).toString(),
        label: 'Total candidates',
        subtext: 'across all roles',
        detail: 'candidates'
      },
      {
        value: numberOrZero(metrics.interviews_completed_this_week).toString(),
        label: 'Completed this week',
        subtext: `${numberOrZero(metrics.interviews_completed)} total completed candidates`,
        detail: 'completed'
      },
      {
        value: averageInviteTime,
        label: 'Avg time to invite',
        subtext: averageInviteDelta,
        detail: 'invite'
      }
    ];
  }, [dashboardData]);

  // Loading state
  if (loading) {
    // Sidebar removed - only show on /app/v1 and /app/v2 routes
    return <PageLoader label="Loading jobs..." activeNav="home" showSidebar={false} />;
  }

  // Error state
  if (error || !dashboardData) {
    return (
      <div className={styles.container}>
        <Sidebar activeNav="home" user={user || undefined} onLogout={handleLogout} />
        <main className={styles.mainContent}>
          <div className={styles.errorContainer}>
            <h2>Failed to load dashboard</h2>
            <p>{error || 'An unexpected error occurred.'}</p>
            <button className={styles.btnRefresh} onClick={handleRefresh}>
              <RefreshIcon sx={{ fontSize: 18 }} />
              Try Again
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Sidebar activeNav="home" user={user || undefined} onLogout={handleLogout} />
      <main className={styles.mainContent}>
        <div className={styles.breadcrumb}>
          <button className={styles.breadcrumbLink} onClick={handleBackToHome}>
            Home
          </button>
          <ChevronRightIcon className={styles.breadcrumbSeparator} />
          <span className={styles.breadcrumbCurrent}>Jobs</span>
        </div>

        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h1 className={styles.title}>Job Roles</h1>
            <p className={styles.subtitle}>All open requisitions — click a job to see its candidates</p>
          </div>
          <div className={styles.headerRight}>
            <div className={styles.searchWrapper}>
              <div className={styles.searchContainer}>
                <SearchIcon className={styles.searchIcon} sx={{ fontSize: 20 }} />
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search jobs, candidates, departments..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                {searchQuery && (
                  <button className={styles.searchClear} onClick={handleClearSearch} aria-label="Clear search">
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </button>
                )}
              </div>
            </div>
            <button className={styles.btnRefresh} onClick={handleRefresh} disabled={loading}>
              <RefreshIcon sx={{ fontSize: 18 }} />
              Refresh
            </button>
            <button className={styles.btnNewJob} onClick={() => setShowNewJobModal(true)}>
              <AddIcon sx={{ fontSize: 18 }} />
              New job
            </button>
          </div>
        </div>

        {!hasSearched && (
          <div className={styles.metricsGrid}>
            {metricCards.map((metric, index) => (
              <MetricCardComponent key={`metric-${index}`} metric={metric} onSelect={setDetailKind} />
            ))}
          </div>
        )}

        {/* Loading state while searching */}
        {globalSearchLoading && hasSearched && (
          <div className={styles.loadingState}>
            <div className={styles.spinner}></div>
            <p>Searching...</p>
          </div>
        )}

        {/* Search results summary - only show when not loading */}
        {!globalSearchLoading &&
          hasSearched &&
          searchQuery &&
          (visibleJobs.length > 0 || visibleCandidates.length > 0) && (
            <div className={styles.searchResults}>
              <p className={styles.searchSummary}>
                Search results for "<strong>{searchQuery}</strong>":
                {visibleJobs.length > 0 && (
                  <>
                    {' '}
                    {visibleJobs.length} job{visibleJobs.length !== 1 ? 's' : ''}
                  </>
                )}
                {visibleJobs.length > 0 && visibleCandidates.length > 0 && <>, </>}
                {visibleCandidates.length > 0 && (
                  <>
                    {' '}
                    {visibleCandidates.length} candidate{visibleCandidates.length !== 1 ? 's' : ''}
                  </>
                )}
              </p>
            </div>
          )}

        {/* Only show results when not loading */}
        {!globalSearchLoading && visibleCandidates.length > 0 && (
          <div className={styles.candidatesSection}>
            <h2 className={styles.sectionTitle}>Candidates ({visibleCandidates.length})</h2>
            <div className={styles.candidatesGrid}>
              {visibleCandidates.map((candidate) => (
                <div
                  key={candidate.candidate_id}
                  className={styles.candidateCard}
                  onClick={() => {
                    if (candidate.job_id) {
                      navigate(`/jobs/${candidate.job_id}/candidates/${candidate.candidate_id}/report`);
                    }
                  }}
                >
                  <div className={styles.candidateHeader}>
                    <h3>{candidate.name}</h3>
                    <span className={`${styles.status} ${styles[`status${candidate.status}`]}`}>
                      {candidate.status.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  </div>
                  <div className={styles.candidateDetails}>
                    <div className={styles.candidateDetail}>
                      <EmailIcon sx={{ fontSize: 16 }} />
                      <span>{candidate.email}</span>
                    </div>
                    <div className={styles.candidateDetail}>
                      <PersonIcon sx={{ fontSize: 16 }} />
                      <span>{candidate.current_title || 'N/A'}</span>
                    </div>
                    <div className={styles.candidateDetail}>
                      <WorkOutlineIcon sx={{ fontSize: 16 }} />
                      <span>{candidate.job_title}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!globalSearchLoading && visibleJobs.length > 0 && (
          <>
            {hasSearched && <h2 className={styles.sectionTitle}>Jobs ({visibleJobs.length})</h2>}
            <div className={styles.jobsGrid}>
              {visibleJobs.map((job) => (
                <JobCardComponent key={job.job_id} job={job} onJobClick={handleJobClick} />
              ))}
            </div>
          </>
        )}

        {hasSearched && visibleJobs.length === 0 && visibleCandidates.length === 0 && !globalSearchLoading && (
          <div className={styles.emptyState}>
            <h3>No results found</h3>
            <p>No jobs or candidates match "{searchQuery}". Try a different search term.</p>
          </div>
        )}

        {!hasSearched && visibleJobs.length === 0 && (
          <div className={styles.emptyState}>
            <h3>No jobs found</h3>
            <p>No jobs available at the moment.</p>
          </div>
        )}
      </main>

      {detailKind && (
        <DashboardDetailsModal
          kind={detailKind}
          jobs={dashboardData.jobs}
          onClose={closeDetails}
          onOpenJob={handleJobClick}
          onOpenCandidate={handleOpenCandidate}
        />
      )}

      <NewJobModal isOpen={showNewJobModal} onClose={() => setShowNewJobModal(false)} onSuccess={handleNewJobSuccess} />
    </div>
  );
};

export default memo(JobsRoles);
